import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { RootState, AppDispatch, store } from '../../store/store';
import { updateObjective, updateMilestoneMap, updateUser } from '../../store/map3PendingSlice';
import { useSelector } from 'react-redux';
import { insertPlaceholderRow } from '../../store/map3UISlice';
import { selectEffectiveZoomLevel } from '../../store/selectors/zoomSelectors';
import { MilestoneMap1, Objective1, Status1, MilestoneMapInterval } from '../../types';
import { Reorder, useDragControls } from 'framer-motion';
import { useState } from 'react';
import { ObjectiveCell3 } from './ObjectiveCell3';
import { StatusCell3 } from './StatusCell3';
import { Pager1 } from './Pager1';
import {
  useKeyboardNavigation,
  GridPosition,
  GridSelection,
  getSelectedPositions,
} from '../../hooks/useKeyboardNavigation';
import { DragHandle } from '../../components_misc/DragHandle';
import { createStatusCellEvent } from '../../utils/statusCellEvents';
import { useMap3WorkspaceContext } from './Map3WorkspaceContext';
import { ObjectivePlaceholderInput } from './ObjectivePlaceholderInput';
import { Dialog } from '../../components_catalyst/dialog';
import { Map3Drawer } from './Map3Drawer';
import { useCurrentMap } from '../../hooks/useCurrentMap';
// import { selectUserRoleForMap } from '../../store/selectors';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import { useParams, useSearchParams } from 'react-router-dom';
import { getIntervalsForMilestoneMap } from '@mm-shared/intervals';
import { PlaylistPlayButton } from './PlaylistPlayButton';
import { HierarchyZoomButton } from './HierarchyZoomButton';
import { useDispatch } from 'react-redux';
import { PlaylistDialog } from './PlaylistDialog';
import { migrateResult1Array } from '@mm-shared/migrations/Result1/migrateResult1';
import { isArrayDifferentAfterMigration } from '@mm-shared/migrations/utils/migrationUtils';
import { formatIntervalDate, formatIntervalDateForScreenReader } from '../../utils/formatDate';
import {
  analyzeObjectiveStatus,
  countObjectiveStatuses,
  formatStatusCounts,
  formatChildStatusCounts,
} from '../../utils/statusSummaryUtils';
import { isMetricObjective, calculateContinuousCode } from '../../utils/kpiUtils';
import { getStatusForAdjustedInterval } from '@mm-shared/intervals';
import { upsertItem } from '../../utils/upsertItem';
import { buildWebStatusWrite } from '../../utils/provenance';
import { buildStatusUpdateForCell } from '../../utils/buildStatusUpdateForCell';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { isParentInMap } from '../../utils/objective-utils';
import {
  getChildObjectivesForParentObjectiveByOrder,
  calculateObjectiveRelationshipUpdates,
  checkForRelationshipDiscrepancies,
} from '@mm-shared/hierarchy';

// ---------------------------------------------------------------------------
// Keyboard rejection hint message constants (Bead: mr-rmit)
// Shown inline above the grid when a user presses a key that does not apply
// to the focused row type. Screen-reader wording replaces the literal "?"
// with "question mark" because the question mark reads inconsistently across
// SR engines.
// ---------------------------------------------------------------------------
const RMIT_METRIC_HINT =
  "Metric rows accept numbers, not letter status codes. Enter number (e.g. '89'), or 'p' to indicate planned or expected in the future.";
const RMIT_METRIC_HINT_SR =
  "Metric rows accept numbers, not letter status codes. Enter a number, for example 89, or p to indicate planned or expected in the future.";
const RMIT_DELIVERABLE_HINT =
  "Deliverable rows accept status codes: g, y, r, p, or ?.";
const RMIT_DELIVERABLE_HINT_SR =
  "Deliverable rows accept status codes: g, y, r, p, or question mark.";

type Map3TableHeaderProps = {
  visibleIntervals: Array<{ start: Date; end: Date }>;
  maxStatusColumnCount: number;
  milestoneMapUuid: string;
  effectiveZoomLevel: MilestoneMapInterval;
};

// Memoize Map3TableHeader to prevent unnecessary re-renders when objectives change
// Only re-render when:
// 1. visibleIntervals change (zoom level or column offset)
// 2. maxStatusColumnCount changes
// 3. milestoneMapUuid changes
// 4. enablePlaylistMode changes (affects playlist buttons)
const Map3TableHeader = React.memo(function Map3TableHeader({
  visibleIntervals,
  maxStatusColumnCount,
  milestoneMapUuid,
  effectiveZoomLevel,
}: Map3TableHeaderProps) {
  const enablePlaylistMode = useSelector(
    (state: RootState) => state.map3UI.mapControls.enablePlaylistMode,
  );

  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === milestoneMapUuid),
  );

  // Function to format the header text based on interval type
  const getHeaderText = (interval: MilestoneMapInterval) => {
    switch (interval) {
      case 'hours':
        return 'Hour';
      case 'days':
        return 'Day';
      case 'weeks':
        return 'Week\nEnding';
      case 'months':
        return 'Month';
      case 'quarters':
        return 'Quarter';
      case 'years':
        return 'Year';
      default:
        return 'Week\nEnding On';
    }
  };

  return (
    <thead>
      <tr>
        <th scope="col" className="w-8 border px-2 py-1" aria-label="Drag handle column"></th>
        <th scope="col" className="border px-2 py-1 text-right align-bottom whitespace-pre-line">
          {getHeaderText(effectiveZoomLevel)}
        </th>
        {visibleIntervals.map((interval, index) => (
          <th
            key={index}
            scope="col"
            className="border px-2 py-1 text-center"
            style={{ minWidth: '40px' }}
            aria-label={`${effectiveZoomLevel} of ${interval.end.toLocaleDateString()}`}
          >
            {enablePlaylistMode ? (
              <PlaylistPlayButton
                makeListFrom="interval"
                milestoneMapUuid={milestoneMapUuid}
                interval={interval.end.toISOString()}
              />
            ) : (
              <div className="h-6" />
            )}
            <div className="flex flex-col items-center">
              {formatIntervalDate(interval.end, effectiveZoomLevel)}
            </div>
          </th>
        ))}
      </tr>
      <tr>
        <th scope="col" className="w-8 border px-2 py-1" aria-label="Drag handle column">
          <span className="sr-only">Reorder controls</span>
        </th>
        <th scope="col" className="border px-2 py-1 text-left">
          Objectives
        </th>
        <th scope="col" className="border px-2 py-1" colSpan={maxStatusColumnCount}>
          Status, Inchstones & Milestones
        </th>
      </tr>
    </thead>
  );
});

type Map3TbodyProps = {
  objectivesOnMap: Objective1[];
  setObjectivesOnMap: (objectives: Objective1[]) => void;
  setAnnouncement: (message: string) => void;
  children: React.ReactNode;
  milestoneMapUuid: string;
};

function Map3Tbody({
  objectivesOnMap,
  setObjectivesOnMap,
  setAnnouncement,
  children,
  milestoneMapUuid,
  pendingObjectivesOrder,
  setPendingObjectivesOrder,
  onDragEndRow,
}: Map3TbodyProps & {
  pendingObjectivesOrder: Objective1[];
  setPendingObjectivesOrder: (order: Objective1[]) => void;
  onDragEndRow?: () => void;
}) {
  // mr-4oag: consume viewVisibilityVersion so Reorder.Group remounts (via key)
  // each time the Outlet transitions from hidden→visible. This discards Framer
  // Motion's stale (0,0,0,0) projection cache and prevents the fly-in animation.
  const { viewVisibilityVersion } = useMap3WorkspaceContext();

  function handleReorder(newOrder: Objective1[]) {
    setPendingObjectivesOrder(newOrder);
    setObjectivesOnMap(newOrder);
    setAnnouncement('Objectives list has been reordered');
    // commitCalledRef is managed in parent
  }
  useEffect(() => {
    setPendingObjectivesOrder(objectivesOnMap);
  }, [objectivesOnMap, setPendingObjectivesOrder]);
  // Only pass onDragEndRow to Map3Tr children
  return (
    <Reorder.Group
      key={viewVisibilityVersion}
      as="tbody"
      axis="y"
      values={pendingObjectivesOrder}
      onReorder={handleReorder}
      aria-label="Draggable objectives list"
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child) && (child.type as any).displayName === 'Map3Tr'
          ? React.cloneElement(child as React.ReactElement<any>, { onDragEndRow })
          : child,
      )}
    </Reorder.Group>
  );
}

type Map3TdObjectiveProps = {
  objective: Objective1;
  index: number;
  focusPosition: { row: number; col: number };
  hoverPosition: { row: number; col: number };
  handleCellFocus: (row: number, col: number) => void;
  handleKeyDown: (e: React.KeyboardEvent, row: number, col: number) => void;
  setHoverPosition: (position: { row: number; col: number }) => void;
  isParent: (objectiveUuid: string) => boolean;
  milestoneMapUuid: string;
  selection: GridSelection;
  handleSelectionStart: (row: number, col: number) => void;
  handleSelectionEnd: (row: number, col: number) => void;
  drawerIsOpen: boolean;
};

function Map3TdObjective({
  objective,
  index,
  focusPosition,
  hoverPosition,
  handleCellFocus,
  handleKeyDown,
  setHoverPosition,
  isParent,
  milestoneMapUuid,
  selection,
  handleSelectionStart,
  handleSelectionEnd,
  drawerIsOpen,
}: Map3TdObjectiveProps) {
  const isSelected = selection && isPositionInSelection({ row: index, col: 0 }, selection);

  return (
    <td
      className={`relative ${isSelected || (!drawerIsOpen && focusPosition.row === index && focusPosition.col === 0) ? 'ring-2 ring-blue-500' : ''}`}
      tabIndex={-1}
      role="gridcell"
      data-row={index}
      data-col={0}
      data-objective-type={objective.type || 'deliverable'}
      data-is-parent={isParent(objective.uuid) || undefined}
      aria-selected={isSelected || (focusPosition.row === index && focusPosition.col === 0)}
      onFocus={() => handleCellFocus(index, 0)}
      onClick={(e) => {
        if (e.shiftKey) {
          handleSelectionEnd(index, 0);
        } else {
          handleCellFocus(index, 0);
          handleSelectionStart(index, 0);
        }
      }}
      onKeyDown={(e) => handleKeyDown(e, index, 0)}
      onMouseEnter={() => setHoverPosition({ row: index, col: 0 })}
      onMouseLeave={() => setHoverPosition({ row: -1, col: -1 })}
    >
      <ObjectiveCell3
        uuid={objective.uuid}
        isParent={isParent(objective.uuid)}
        row={index}
        column={0}
        focusPosition={focusPosition}
        hoverPosition={hoverPosition}
        milestoneMapUuid={milestoneMapUuid}
      />
    </td>
  );
}

type Map3TdStatusProps = {
  objective: Objective1;
  index: number;
  statusIndex: number;
  columnOffset: number;
  visibleIntervals: Array<{ start: Date; end: Date }>;
  focusPosition: { row: number; col: number };
  hoverPosition: { row: number; col: number };
  handleCellFocus: (row: number, col: number) => void;
  handleKeyDown: (e: React.KeyboardEvent, row: number, col: number) => void;
  setHoverPosition: (position: { row: number; col: number }) => void;
  isParent: (objectiveUuid: string) => boolean;
  milestoneMapUuid: string;
  selection: GridSelection;
  handleSelectionStart: (row: number, col: number) => void;
  handleSelectionEnd: (row: number, col: number) => void;
  ariaRowIndex: number;
  ariaColIndex: number;
  originalInterval: MilestoneMapInterval;
  effectiveZoomLevel: MilestoneMapInterval;
  drawerIsOpen: boolean;
};

function Map3TdStatus({
  objective,
  index,
  statusIndex,
  columnOffset,
  visibleIntervals,
  focusPosition,
  hoverPosition,
  handleCellFocus,
  handleKeyDown,
  setHoverPosition,
  isParent,
  milestoneMapUuid,
  selection,
  handleSelectionStart,
  handleSelectionEnd,
  ariaRowIndex,
  ariaColIndex,
  originalInterval,
  effectiveZoomLevel,
  drawerIsOpen,
}: Map3TdStatusProps) {
  const currentInterval = visibleIntervals[statusIndex];

  // Efficient status lookup: getStatusForAdjustedInterval handles zoom inference
  // Time complexity: O(n) where n = number of statuses for this objective
  // This is optimal as we must examine statuses to determine inheritance
  const status = getStatusForAdjustedInterval(
    objective.statuses || [],
    currentInterval,
    originalInterval,
    effectiveZoomLevel,
  );

  const isSelected =
    selection && isPositionInSelection({ row: index, col: statusIndex + 1 }, selection);

  const StatusCellComponent = StatusCell3;

  return (
    <td
      className={`${isSelected || (!drawerIsOpen && focusPosition.row === index && focusPosition.col === statusIndex + 1) ? 'ring-2 ring-blue-500' : ''}`}
      tabIndex={-1}
      role="gridcell"
      data-row={index}
      data-col={statusIndex + 1}
      aria-rowindex={ariaRowIndex}
      aria-colindex={ariaColIndex}
      aria-selected={
        isSelected || (focusPosition.row === index && focusPosition.col === statusIndex + 1)
      }
      aria-label={`Status for ${objective.text} at interval ${
        statusIndex + columnOffset + 1
      }: ${status?.statusCode || 'empty'}`}
      onFocus={() => handleCellFocus(index, statusIndex + 1)}
      onClick={(e) => {
        if (e.shiftKey) {
          handleSelectionEnd(index, statusIndex + 1);
        } else {
          handleCellFocus(index, statusIndex + 1);
          handleSelectionStart(index, statusIndex + 1);
        }
      }}
      onKeyDown={(e) => handleKeyDown(e, index, statusIndex + 1)}
      onMouseEnter={() => setHoverPosition({ row: index, col: statusIndex + 1 })}
      onMouseLeave={() => setHoverPosition({ row: -1, col: -1 })}
    >
      <StatusCellComponent
        objectiveUuid={objective.uuid}
        objectiveIsParent={isParent(objective.uuid)}
        statusUuid={status?.uuid || ''}
        row={index}
        column={statusIndex + 1}
        focusPosition={focusPosition}
        hoverPosition={hoverPosition}
        forIntervalEnding={visibleIntervals[statusIndex].end.toISOString()}
        milestoneMapUuid={milestoneMapUuid}
        effectiveZoomLevel={effectiveZoomLevel}
      />
    </td>
  );
}

// Helper function to check if a position is within a selection range
function isPositionInSelection(pos: GridPosition, sel: GridSelection): boolean {
  if (!sel) return false;

  // Single-cell "selection" (anchor point from click) is not a visual selection
  if (sel.start.row === sel.end.row && sel.start.col === sel.end.col) return false;

  const minRow = Math.min(sel.start.row, sel.end.row);
  const maxRow = Math.max(sel.start.row, sel.end.row);
  const minCol = Math.min(sel.start.col, sel.end.col);
  const maxCol = Math.max(sel.start.col, sel.end.col);

  return pos.row >= minRow && pos.row <= maxRow && pos.col >= minCol && pos.col <= maxCol;
}

/**
 * IMPORTANT: Framer Motion Drag Controls Pattern
 *
 * For drag functionality to work correctly, the following must be in the same component:
 * 1. useDragControls hook
 * 2. Reorder.Item component
 * 3. Drag handle UI (with onPointerDown)
 *
 * They can be separate from Reorder.Group, but each draggable item needs its own
 * instance of useDragControls.
 *
 * This pattern ensures:
 * - Proper hook usage (React rules of hooks)
 * - Working drag functionality
 * - Clean component organization
 */
type Map3TrProps = {
  objective: Objective1;
  isParent: boolean;
  children: React.ReactNode;
  milestoneMapUuid: string;
  onDragEndRow?: () => void;
};

function Map3Tr({ milestoneMapUuid, objective, isParent, children, onDragEndRow }: Map3TrProps) {
  const controls = useDragControls();

  // Check user's role for drag permissions
  const currentMap = useCurrentMap();
  // const userRole = useSelector((state: RootState) =>
  //   currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  // );
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // In playlist mode, we don't want to allow dragging rows.
  const enablePlaylistMode = useSelector(
    (state: RootState) => state.map3UI.mapControls.enablePlaylistMode,
  );

  // In hierarchy zoom mode, show zoom buttons instead of drag handles
  const enableHierarchyZoomMode = useSelector(
    (state: RootState) => state.map3UI.mapControls.enableHierarchyZoomMode,
  );

  // Helper to get zoom link for an objective
  const getZoomLink = (objectiveUuid: string, direction: 'in' | 'out'): string | undefined => {
    const link = currentMap?.hierarchyZoomLinks?.find((l) => l.objectiveUuid === objectiveUuid);
    return direction === 'in' ? link?.zoomInMapShortId : link?.zoomOutMapShortId;
  };

  // Disable dragging for playlist mode, hierarchy zoom mode, or non-editors
  return enablePlaylistMode || enableHierarchyZoomMode || !canEdit ? (
    <tr>
      <td className="w-8 text-center align-middle">
        <div className="flex justify-center">
          {enableHierarchyZoomMode && !enablePlaylistMode ? (
            <HierarchyZoomButton
              direction={isParent ? 'out' : 'in'}
              targetMapShortId={getZoomLink(objective.uuid, isParent ? 'out' : 'in')}
            />
          ) : enablePlaylistMode && !isParent ? (
            <PlaylistPlayButton
              makeListFrom="objective"
              milestoneMapUuid={milestoneMapUuid}
              objectiveUuid={objective.uuid}
            />
          ) : (
            // Show empty space for non-editors (no drag handle, no playlist button)
            <div className="h-6 w-6 m-auto p-1" />
          )}
        </div>
      </td>
      {children}
    </tr>
  ) : (
    <Reorder.Item
      as="tr"
      key={objective.uuid}
      value={objective}
      dragListener={false}
      dragControls={controls}
      aria-label={`Draggable objective: ${objective.text}`}
      onDragEnd={onDragEndRow}
    >
      <td className="w-8 px-2">
        <div className="cursor-grab" onPointerDown={(e) => controls.start(e)}>
          <DragHandle className="h-6 w-6 m-auto p-1 stroke-gray-400" />
        </div>
      </td>
      {children}
    </Reorder.Item>
  );
}

/**
 * A placeholder row that appears at the bottom of the milestone map grid.
 * This row allows users to add new objectives and maintains keyboard navigation
 * consistency with the rest of the grid.
 *
 * Unlike Map3Tr rows which contain actual objectives, this row:
 * - Is not draggable
 * - Contains empty status cells
 * - Has a special input cell for adding new objectives
 * - Must maintain the same keyboard navigation pattern as regular rows
 */
interface Map3TrPlaceholderProps {
  index: number;
  columnCount: number;
  milestoneMapUuid: string;
  // Below are navigation props to maintain grid keyboard accessibility
  focusPosition: GridPosition;
  hoverPosition: GridPosition;
  handleCellFocus: (row: number, col: number) => void;
  handleKeyDown: (e: React.KeyboardEvent, row: number, col: number) => void;
  setHoverPosition: (position: GridPosition) => void;
  handleSelectionStart: (row: number, col: number) => void;
  handleSelectionEnd: (row: number, col: number) => void;
}

function Map3TrPlaceholder({
  index,
  columnCount,
  milestoneMapUuid,
  focusPosition,
  hoverPosition,
  handleCellFocus,
  handleKeyDown,
  setHoverPosition,
  handleSelectionStart,
  handleSelectionEnd,
}: Map3TrPlaceholderProps) {
  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  // const userRole = useSelector((state: RootState) =>
  //   currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  // );
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  return (
    <tr className="h-8">
      {/* Empty drag handle cell to maintain column alignment */}
      <td className="w-8 px-2">
        <div className="h-6 w-6 m-auto p-1" />
      </td>
      {/* Input cell for adding new objectives - only show for editors/owners */}
      <td
        className="truncate"
        tabIndex={-1}
        role="gridcell"
        data-row={index}
        data-col={0}
        aria-selected={focusPosition.row === index && focusPosition.col === 0}
        onMouseEnter={() => setHoverPosition({ row: index, col: 0 })}
        onMouseLeave={() => setHoverPosition({ row: -1, col: -1 })}
        onKeyDown={(e) => {
          // Handle Enter key to activate parent button
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            const parentButton = e.currentTarget.querySelector(
              '[data-testid="add-parent-button"]',
            ) as HTMLElement;
            if (parentButton) {
              parentButton.click();
            }
            return;
          }
          // Let grid handle arrow keys and Tab for consistent keyboard navigation
          handleKeyDown(e, index, 0);
        }}
      >
        {canEdit ? (
          <ObjectivePlaceholderInput
            row={index}
            milestoneMapUuid={milestoneMapUuid}
            handleKeyDown={handleKeyDown}
            handleCellFocus={handleCellFocus}
          />
        ) : (
          <span className="sr-only">Add objectives restricted for your role</span>
        )}
      </td>
      {/* Empty status cells — focusable for keyboard navigation consistency */}
      {Array(columnCount - 2)
        .fill(null)
        .map((_, i) => (
          <td
            key={i}
            tabIndex={-1}
            role="gridcell"
            data-row={index}
            data-col={i + 1}
            aria-selected={focusPosition.row === index && focusPosition.col === i + 1}
            aria-label={`Empty placeholder cell at interval ${i + 1}`}
            onKeyDown={(e) => handleKeyDown(e, index, i + 1)}
            onMouseEnter={() => setHoverPosition({ row: index, col: i + 1 })}
            onMouseLeave={() => setHoverPosition({ row: -1, col: -1 })}
          >
            <span className="sr-only">Empty placeholder cell</span>
          </td>
        ))}
    </tr>
  );
}

type Map3DebugPanelProps = {
  debugLog: Array<{
    timestamp: number;
    action: string;
    details: unknown;
  }>;
  onClear: () => void;
};

function Map3DebugPanel({ debugLog, onClear }: Map3DebugPanelProps) {
  return (
    <div
      className="fixed bottom-0 right-0 bg-white border border-gray-200 p-4 m-4 rounded shadow-lg max-h-96 overflow-auto"
      style={{ zIndex: 50, maxWidth: '400px' }}
    >
      <h3 className="font-bold mb-2">Debug Log</h3>
      <button onClick={onClear} className="text-xs bg-red-100 px-2 py-1 rounded mb-2">
        Clear Log
      </button>
      <div className="text-xs space-y-1">
        {debugLog.map((entry, i) => (
          <div key={i} className="border-b border-gray-100 pb-1">
            <span className="text-gray-500">
              {new Date(entry.timestamp).toISOString().split('T')[1]}
            </span>
            <span className="font-bold mx-2">{entry.action}</span>
            <span className="text-gray-700">{JSON.stringify(entry.details)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Map3ErrorMessage — inline hint/error strip rendered above the Map3 grid.
 *
 * Catalyst divergence (Bead: mr-rmit): This is a pre-existing local primitive
 * that predates the Catalyst design system. Catalyst ships no Toast, Snackbar,
 * or Popover primitive — only Alert, which is a modal Dialog with incorrect
 * semantics for a typo-level corrective hint. Extending this local component
 * is the correct approach per the catalyst-default-with-divergence policy.
 *
 * Severity values:
 * - 'error' (default): red palette, role=alert — used for keyboard navigation errors
 * - 'hint': amber palette, role=status — used for keypress rejection hints (mr-rmit)
 */
type Map3ErrorMessageProps = {
  error: string | null;
  severity?: 'error' | 'hint';
};

function Map3ErrorMessage({ error, severity = 'error' }: Map3ErrorMessageProps) {
  if (!error) return null;

  if (severity === 'hint') {
    return (
      <div
        role="status"
        className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-900 text-sm px-4 py-2 rounded"
      >
        <InformationCircleIcon className="h-4 w-4 text-amber-600 flex-shrink-0" aria-hidden="true" />
        {error}
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
    >
      {error}
    </div>
  );
}

function Map3KeyboardHelp({ show, onClose }: { show: boolean; onClose: () => void }) {
  // Store reference to the element that had focus before dialog opened
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (show) {
      // Store the current active element
      previousFocus.current = document.activeElement as HTMLElement;
    } else if (previousFocus.current) {
      // Return focus when dialog closes
      previousFocus.current.focus();
    }
  }, [show]);

  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const keyboardShortcuts = [
    {
      section: 'Global Navigation',
      shortcuts: [
        { key: 'Tab', description: 'Move between page sections (toolbar, table, pager)' },
        { key: 'Enter (on table)', description: 'Enter grid navigation mode' },
      ],
    },
    {
      section: 'Grid Navigation Mode',
      shortcuts: [
        { key: '←↑↓→', description: 'Navigate cell by cell across entire grid' },
        { key: 'Tab', description: 'Jump between objectives (row by row)' },
        { key: 'Home', description: 'Move to first cell in row' },
        { key: 'End', description: 'Move to last cell in row' },
        { key: 'Ctrl+Home', description: 'Move to first cell in grid' },
        { key: 'Ctrl+End', description: 'Move to last cell in grid' },
        { key: 'Enter', description: 'Inspect or edit current objective or status cell' },
        { key: 'Esc', description: 'Exit grid navigation mode' },
      ],
    },
    {
      section: 'Action Bar Mode',
      shortcuts: [
        { key: '←→', description: 'Navigate between actions' },
        { key: 'Enter/Space', description: 'Activate action' },
        { key: 'Esc', description: 'Return to grid navigation' },
      ],
    },
    {
      section: 'Status Colors',
      shortcuts: [
        { key: 'g', description: 'Set status to green' },
        { key: 'y', description: 'Set status to yellow' },
        { key: 'r', description: 'Set status to red' },
        { key: 'p', description: 'Set status to gray' },
        { key: '?', description: 'Set status to light gray' },
      ],
    },
  ];

  if (!show) return null;

  return (
    <Dialog open={show} onClose={onClose} aria-label="Keyboard shortcuts">
      <div className="p-4 max-w-md">
        <h2 className="text-xl font-semibold mb-4">Keyboard Shortcuts</h2>
        <div className="space-y-6">
          {keyboardShortcuts.map(({ section, shortcuts }) => (
            <div key={section}>
              <h3 className="font-medium text-gray-900 mb-2">{section}</h3>
              <div className="space-y-2">
                {shortcuts.map(({ key, description }) => (
                  <div key={key} className="flex items-center gap-3">
                    <kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{key}</kbd>
                    <span className="text-gray-700">{description}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-sm text-gray-500">
          Press{' '}
          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">
            {isMac ? '⌘' : 'Ctrl'}+/
          </kbd>{' '}
          to toggle this help
        </div>
      </div>
    </Dialog>
  );
}

type Map3DebugIndicatorProps = {
  debug: boolean;
};

function Map3DebugIndicator({ debug }: Map3DebugIndicatorProps) {
  if (!debug) return null;

  return (
    <div
      className="absolute top-0 left-0 right-0 bg-yellow-100 text-yellow-800 px-2 py-1 text-xs text-center"
      style={{ zIndex: 100 }}
    >
      Debug Mode
    </div>
  );
}

/**
 * Floating overlay that shows the current numeric input value while the user
 * types digits into a metric objective cell.
 */
function MetricInputOverlay({
  tableRef,
  row,
  col,
  digits,
}: {
  tableRef: React.RefObject<HTMLTableElement>;
  row: number;
  col: number;
  digits: string;
}) {
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const cell = tableRef.current?.querySelector(`[data-row="${row}"][data-col="${col}"]`) as HTMLElement;
    if (cell && tableRef.current) {
      const cellRect = cell.getBoundingClientRect();
      const tableRect = tableRef.current.getBoundingClientRect();
      setPosition({
        top: cellRect.top - tableRect.top,
        left: cellRect.left - tableRect.left,
        width: cellRect.width,
      });
    }
  }, [tableRef, row, col]);

  if (!position) return null;

  return (
    <div
      className="absolute z-50 pointer-events-none flex items-center justify-center"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        height: 32,
      }}
    >
      <div className="bg-blue-600 text-white text-sm font-mono font-bold px-2 py-0.5 rounded shadow-lg">
        {digits}
      </div>
    </div>
  );
}

interface Map3Props {
  onReady?: () => void;
}

/**
 * Map3 is a milestone map built with a HTML table element.
 *
 * This component creates:
 * - a basic accessible HTML table, mapping milestone map data to the table cells
 * - tab order,
 * - keyboard navigation,
 * - reordering objectives with draggable rows
 * - a pager for navigating forward/backward between weeks/intervals
 *
 * ## Performance Optimizations for Zoom Changes
 *
 * To ensure smooth zoom transitions (<300ms target with 100 objectives):
 *
 * 1. **Memoized Interval Generation** (lines 890-898)
 *    - Uses useMemo to cache interval calculations
 *    - Only recalculates when: map UUID, startDate, endDate, or effectiveZoomLevel changes
 *    - Prevents expensive date calculations on every render
 *
 * 2. **Memoized Table Header** (lines 63-138)
 *    - Uses React.memo to prevent re-renders when only objectives change
 *    - Only re-renders when intervals, zoom level, or playlist mode changes
 *    - Significantly reduces render work for large objective lists
 *
 * 3. **Efficient Status Lookups** (lines 301-306)
 *    - Uses getStatusForAdjustedInterval for O(n) status lookups per objective
 *    - Handles zoom-level inheritance without additional overhead
 *    - Optimal complexity as all statuses must be examined for inheritance logic
 *
 * **Measured Performance**: With these optimizations, zoom changes complete in
 * <200ms for maps with 100+ objectives on standard hardware.
 *
 * @param shortId - The short ID of the map to display. (Short ID is the last 10 characters of the UUID.)
 * @returns A React component that displays the map3 grid.
 */
function Map3Component({ onReady }: Map3Props) {
  const [announcement, setAnnouncement] = useState('');
  const [metricInput, setMetricInput] = useState<{ row: number; col: number; digits: string } | null>(null);
  const [undoStack, setUndoStack] = useState<Array<{ objectiveUuid: string; previousStatuses: Status1[] }[]>>([]);
  const { mapShortId } = useParams();
  const [map3SearchParams] = useSearchParams();
  const drawerIsOpen = map3SearchParams.get('drawer') === 'objective';

  const dispatch = useDispatch<AppDispatch>();
  const isLoading = useSelector((state: RootState) => state.map3Data.isLoading);
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);

  const allLoadedObjectives = useSelector((state: RootState) => state.map3Data.objectives);

  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.shortId === mapShortId),
  );

  // Permission check — used in keyboard handlers to guard write operations
  const userRole = useRoleWithOverride(milestoneMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // Initialize objectivesOnMap synchronously from the Redux store so Map3 never
  // renders a blank grid on initial mount (or remount when switching route segments).
  // The lazy initializer reads from the store directly at mount time; the useEffect
  // below keeps it in sync with Redux for subsequent updates (drag, reorder, etc.).
  const [objectivesOnMap, setObjectivesOnMap] = useState<Objective1[]>(() => {
    if (!mapShortId) return [];
    const initialState = store.getState();
    const initialMap = initialState.map3Data.milestoneMaps.find(
      (m) => m.shortId === mapShortId,
    );
    if (!initialMap?.objectiveUuids) return [];
    return initialMap.objectiveUuids
      .map((uuid) => initialState.map3Data.objectives.find((obj) => obj.uuid === uuid))
      .filter((obj): obj is Objective1 => obj !== undefined);
  });

  // Get effective zoom level (current zoom or map's base interval)
  const effectiveZoomLevel = useSelector((state: RootState) =>
    selectEffectiveZoomLevel(state, milestoneMap?.interval || 'weeks'),
  );

  // Synchronize local objectivesOnMap state with Redux state
  // This effect runs when:
  // 1. Initial load: When milestone map and objectives are first loaded from Redux
  // 2. After drag operations: When milestoneMap.objectiveUuids changes due to reordering
  // 3. After relationship updates: When allLoadedObjectives changes due to parent/child updates
  // 4. When objectives are added/removed from the map
  useEffect(() => {
    if (milestoneMap?.objectiveUuids && allLoadedObjectives) {
      const lookupObjectives = milestoneMap.objectiveUuids
        .map((uuid) => allLoadedObjectives.find((obj) => obj.uuid === uuid))
        .filter((obj): obj is Objective1 => obj !== undefined);

      setObjectivesOnMap(lookupObjectives);
    }
  }, [milestoneMap?.objectiveUuids, allLoadedObjectives]);

  // Track when we're in a relationship migration to prevent loops
  const relationshipMigrationRef = useRef(false);

  // Store map UUID for stable cleanup reference
  const mapUuidRef = useRef<string | null>(null);

  // Update ref when map changes
  useEffect(() => {
    if (milestoneMap?.uuid) {
      mapUuidRef.current = milestoneMap.uuid;
    }
  }, [milestoneMap?.uuid]);

  // On-demand migration: Check and fix parent/child relationship discrepancies
  // (where the relationship implied by milestoneMap.objectiveUuids and milestoneMap.parentUuids
  // is not reflected in objectives' .parentIds and .childIds properties).
  // This provides a backstop to ensure relationship data integrity as features stabilize
  useEffect(() => {
    if (!milestoneMap || objectivesOnMap.length === 0) return;

    // PREVENT INFINITE LOOP: Don't run migration if we're already in one
    if (relationshipMigrationRef.current) return;

    const { hasDiscrepancies, updates } = checkForRelationshipDiscrepancies(
      milestoneMap,
      objectivesOnMap,
    );

    if (hasDiscrepancies) {
      console.warn(
        `[Map3] Relationship migration: Fixed ${Object.keys(updates).length} objectives on map ${milestoneMap.shortId || milestoneMap.uuid}`,
        {
          mapTitle: milestoneMap.title,
          mapShortId: milestoneMap.shortId,
          discrepancies: updates,
          affectedObjectives: Object.keys(updates),
        },
      );

      // Set flag to prevent re-entry during the updates
      relationshipMigrationRef.current = true;

      // Apply the calculated updates to fix discrepancies
      Object.entries(updates).forEach(([objectiveUuid, objectiveUpdates]) => {
        dispatch(updateObjective({ uuid: objectiveUuid, updates: objectiveUpdates }));
      });

      // Reset flag after a short delay to allow updates to complete
      setTimeout(() => {
        relationshipMigrationRef.current = false;
      }, 1000);
    }
  }, [dispatch, milestoneMap, objectivesOnMap]);

  // TODO: CLEANUP DISABLED - Component unmounts after every Firestore update
  // The component is remounting (not just effect re-running) which breaks real-time sync
  // Need to investigate why component unmounts after handleChange processes Firestore updates
  // For MVP: Real-time sync works, listeners may leak on navigation (acceptable short-term)

  // useEffect(() => {
  //   return () => {
  //     if (mapUuidRef.current) {
  //       console.log('[Map3] Cleaning up collaboration listeners for map:', mapUuidRef.current);
  //       dispatch(cleanupMapListeners({ mapUuid: mapUuidRef.current }));
  //     }
  //   };
  // }, []);

  const isParent = useCallback(
    (objectiveUuid: string): boolean => {
      return milestoneMap ? isParentInMap(objectiveUuid, milestoneMap) : false;
    },
    [milestoneMap],
  );

  // TODO
  // Revisit this. Determine if maxStatusColumnCount should be dynamic,
  // how it relates to suppor for different screen sizes, if it's something
  // users can set, etc.
  //
  // Set the maximum number of status columns to display. Overflowing columns
  // are hidden and accessed via the pager.
  const maxStatusColumnCount = 13;

  // columnOffset determines which status columns are currently visible,
  // acting as a sliding window over all available intervals. When users page
  // forward/backward, this offset changes to show different sets of columns.
  // For example, with offset=0, columns 0-12 are shown; with offset=1,
  // columns 1-13 are shown, and so on. The offset increases/decreases by 1
  // each time the user clicks next/previous.
  const [columnOffset, setColumnOffset] = useState(0);

  // Memoize interval generation to prevent unnecessary recalculations
  // Intervals only need to regenerate when:
  // 1. Map date range changes (startDate/endDate)
  // 2. Zoom level changes (effectiveZoomLevel)
  // 3. Map instance changes (uuid)
  const allIntervals = useMemo(() => {
    if (!milestoneMap) return [];
    const intervals = getIntervalsForMilestoneMap(milestoneMap, effectiveZoomLevel);
    console.debug('[Zoom] Intervals generated:', {
      zoomLevel: effectiveZoomLevel,
      intervalCount: intervals.length,
      mapUuid: milestoneMap.uuid,
      dateRange: {
        start: milestoneMap.startDate,
        end: milestoneMap.endDate,
      },
    });
    return intervals;
  }, [
    milestoneMap?.uuid,
    milestoneMap?.startDate,
    milestoneMap?.endDate,
    effectiveZoomLevel,
  ]);

  // Get only the intervals for the current page
  const visibleIntervals = allIntervals.slice(columnOffset, columnOffset + maxStatusColumnCount);

  // Calculate if we can navigate forward/backward
  const canGoBack = columnOffset > 0;
  const canGoForward = columnOffset + maxStatusColumnCount < allIntervals.length;

  const handlePreviousPage = () => {
    if (canGoBack) {
      setColumnOffset(Math.max(0, columnOffset - 1));
      setAnnouncement('Moved to previous week');
    }
  };

  const handleNextPage = () => {
    if (canGoForward) {
      setColumnOffset(columnOffset + 1);
      setAnnouncement('Moved to next week');
    }
  };

  const tableRef = useRef<HTMLTableElement>(null);

  const [keyboardError, setKeyboardError] = useState<string | null>(null);
  const [keyboardErrorSeverity, setKeyboardErrorSeverity] = useState<'error' | 'hint'>('error');
  // Ref for the keyboardError auto-dismiss timer. Stored in a ref so we can
  // clearTimeout on subsequent calls, preventing stale timers from clearing
  // fresh messages when keys are pressed in rapid succession. (Bead: mr-rmit)
  const keyboardErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  /**
   * Show a keyboard hint above the grid and auto-dismiss after 3000ms.
   * Uses last-write-wins: clears any in-flight timer before setting a new one.
   * Pass srText when the screen-reader announcement needs different wording
   * (e.g. "Slash" instead of "?" for SR engine compatibility). (Bead: mr-rmit)
   */
  const showKeyboardHint = useCallback(
    (message: string, srText?: string) => {
      if (keyboardErrorTimerRef.current) {
        clearTimeout(keyboardErrorTimerRef.current);
      }
      setKeyboardError(message);
      setKeyboardErrorSeverity('hint');
      setAnnouncement(srText ?? message);
      keyboardErrorTimerRef.current = setTimeout(() => setKeyboardError(null), 3000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Debug mode can be enabled by adding ?debug=true to the URL
  // This allows keyboard navigation events to be logged to console
  // without requiring code changes, even in production.
  // No sensitive data is logged - only focus changes, key presses,
  // and validation errors to help with troubleshooting.
  const debug = new URLSearchParams(window.location.search).get('debug') === 'true';

  // Enhanced debug state
  const [debugLog, setDebugLog] = useState<
    Array<{
      timestamp: number;
      action: string;
      details: unknown;
    }>
  >([]);

  const addDebugLog = useCallback(
    (action: string, details: unknown) => {
      if (debug) {
        setDebugLog((prev) => [
          ...prev,
          {
            timestamp: Date.now(),
            action,
            details,
          },
        ]);
      }
    },
    [debug],
  );

  // Clear debug log after 1000 entries to prevent memory issues
  useEffect(() => {
    if (debugLog.length > 1000) {
      setDebugLog((prev) => prev.slice(-500));
    }
  }, [debugLog]);

  const [hoverPosition, setHoverPosition] = useState({ row: -1, col: -1 });

  const placeholderRows = useSelector((state: RootState) => state.map3UI.placeholderRows);

  // Helper function to generate table summary for accessibility (ARIA announcements)
  const generateTableSummary = useCallback(() => {
    const mapTitle = milestoneMap?.title || 'Untitled milestone map';

    // Only count child objectives (not parents) for the summary
    const childObjectives = objectivesOnMap.filter((obj) => !isParent(obj.uuid));
    const rowCount = childObjectives.length;

    if (rowCount === 0) {
      return `Milestone Map name: ${mapTitle}. No work forecasted or reported.`;
    }

    // Use utility to count statuses across all child objectives
    const statusCounts = countObjectiveStatuses(childObjectives);
    const statusSummary = formatStatusCounts(statusCounts, rowCount);

    if (statusSummary === '') {
      return `Milestone Map name: ${mapTitle}. No work forecasted or reported.`;
    }

    return `Milestone Map name: ${mapTitle}. ${rowCount} rows. Latest status reported: ${statusSummary}.`;
  }, [milestoneMap?.title, objectivesOnMap, isParent]);

  // Helper function to generate objective summary for accessibility (ARIA announcements)
  const generateObjectiveSummary = useCallback(
    (objective: Objective1) => {
      const objectiveTitle = objective.text;

      if (isParent(objective.uuid)) {
        // For parent objectives: count children and their statuses
        const children = objectivesOnMap.filter((obj) => obj.parentIds?.includes(objective.uuid));
        const childCount = children.length;

        if (childCount === 0) {
          return `${objectiveTitle}. Has no children.`;
        }

        // Use utility to count statuses across children (only actual work statuses for parents)
        const statusCounts = countObjectiveStatuses(children);
        const childStatusSummary = formatChildStatusCounts(statusCounts);

        let summary = `${objectiveTitle}. Has ${childCount} children.`;
        if (childStatusSummary) {
          summary += ` ${childStatusSummary}.`;
        }

        return summary;
      } else {
        // For child objectives: latest status with date
        const analysis = analyzeObjectiveStatus(objective);

        if (analysis.type === 'none') {
          return `${objectiveTitle}. ${analysis.displayName}.`;
        }

        const dateText = analysis.date ? `, as of ${analysis.date}` : '';
        return `${objectiveTitle}. Latest status ${analysis.displayName}${dateText}.`;
      }
    },
    [objectivesOnMap, isParent],
  );

  const {
    focusPosition,
    selection,
    handleCellFocus,
    handleKeyDown,
    handleSelectionStart,
    handleSelectionEnd,
  } = useKeyboardNavigation({
    tableRef,
    rowCount: objectivesOnMap.length + placeholderRows.length,
    columnCount: visibleIntervals.length + 1,
    debug: debug,
    strictValidation: process.env.NODE_ENV === 'development',
    onError: (error) => {
      // Fix latent timer leak (Bead: mr-rmit): clear any pending dismiss timer
      // before setting a new one to prevent stale timers from clearing fresh messages.
      if (keyboardErrorTimerRef.current) {
        clearTimeout(keyboardErrorTimerRef.current);
      }
      setKeyboardError(error.message);
      setKeyboardErrorSeverity('error');
      addDebugLog('error', error);
      keyboardErrorTimerRef.current = setTimeout(() => setKeyboardError(null), 3000);

      // In production, log keyboard navigation errors to console for debugging
      // In development, errors are handled by strictValidation throwing errors
      // In test/other environments, errors are silently handled
      if (process.env.NODE_ENV === 'production') {
        console.error('[Map3] Keyboard navigation error:', error);
      }
    },
    onFocusChange: (position: GridPosition) => {
      // Clear any pending hint/error dismiss timer on focus change
      if (keyboardErrorTimerRef.current) {
        clearTimeout(keyboardErrorTimerRef.current);
        keyboardErrorTimerRef.current = null;
      }
      setKeyboardError(null);
      addDebugLog('focusChange', position);

      if (position.row === -1) {
        setAnnouncement(
          'Exited grid navigation. Press Enter to re-enter grid or Tab to continue to next section.',
        );
        return;
      }

      // Check if we're on the placeholder row
      if (position.row >= objectivesOnMap.length) {
        setAnnouncement('Add new objective row');
        return;
      }

      const objective = objectivesOnMap[position.row];
      let announcement = '';

      if (position.col === 0) {
        // Enhanced objective announcement with status summary
        announcement = generateObjectiveSummary(objective);
      } else {
        const interval = visibleIntervals[position.col - 1];
        const dateText = formatIntervalDateForScreenReader(
          interval.end,
          milestoneMap?.interval || 'weeks',
        );

        // Get the status for this interval with zoom inference
        const status = getStatusForAdjustedInterval(
          objective.statuses || [],
          interval,
          milestoneMap?.interval || 'weeks',
          effectiveZoomLevel,
        );

        // TODO: In the future, we should enhance statusSummaryUtils.ts to provide:
        // 1. statusColor: A human-readable color name (green, yellow, red)
        // 2. statusDescription: A description of what the status means
        // This will allow for more meaningful announcements like:
        // "Status for [objective] at [date]: Green - On track with no issues"
        let statusText = 'no status';
        if (status?.statusCode) {
          switch (status.statusCode.toLowerCase()) {
            case 'g':
              statusText = 'green';
              break;
            case 'y':
              statusText = 'yellow';
              break;
            case 'r':
              statusText = 'red';
              break;
            case 'p':
              statusText = 'planned';
              break;
            case '?':
              statusText = 'TBD';
              break;
          }
        }

        announcement = `Status for ${objective.text} at ${dateText}: ${statusText}`;
      }

      setAnnouncement(announcement);
    },
    onActivate: (position: GridPosition) => {
      const selector = `[data-row="${position.row}"][data-col="${position.col}"]`;
      const cell = tableRef.current?.querySelector(selector) as HTMLElement;

      if (position.col === 0) {
        // For objective cells, find the ObjectiveCell2 div and dispatch Enter to it
        const objectiveDiv = cell?.querySelector('[data-objective-cell]') as HTMLElement;
        if (objectiveDiv) {
          const enterEvent = new KeyboardEvent('keydown', {
            key: 'Enter',
            bubbles: true,
            cancelable: true,
          });
          objectiveDiv.dispatchEvent(enterEvent);
        }
      } else {
        // For status cells, open tooltip
        const statusCell = cell?.querySelector('[data-status-cell]') as HTMLElement;
        if (statusCell) {
          const openEvent = createStatusCellEvent('openTooltip', undefined);
          statusCell.dispatchEvent(openEvent);
        }
      }
    },
    onKeyPress: (key: string, position: GridPosition) => {
      addDebugLog('keyPress', { key, position });

      // Handle Enter/Space for opening tooltip
      if (key === 'Enter' || key === ' ') {
        const selector = `[data-row="${position.row}"][data-col="${position.col}"]`;
        const cell = tableRef.current?.querySelector(selector) as HTMLElement;
        const statusCell = cell?.querySelector('[data-status-cell]') as HTMLElement;
        if (statusCell) {
          const openEvent = createStatusCellEvent('openTooltip', undefined);
          statusCell.dispatchEvent(openEvent);
          return;
        }
      }

      // Determine the status code for the key press
      const validStatusCodes: Status1['statusCode'][] = ['g', 'y', 'r', 'p', '?'];
      const pressedKey = key.toLowerCase() as Status1['statusCode'];
      const isStatusKey = validStatusCodes.includes(pressedKey);
      const isDeleteKey = key === 'Delete' || key === 'Backspace';

      // For bulk selection: build merged updates per objective to avoid race conditions.
      // Each StatusCell2 independently reads objective.statuses and dispatches the full array,
      // so concurrent dispatches for the same objective overwrite each other.
      // Instead, we collect all status changes per objective and dispatch once.
      if (selection && (isStatusKey || isDeleteKey)) {
        const positions = getSelectedPositions(selection);

        // Group positions by row (objective)
        const byRow: Record<number, number[]> = {}; // row -> [col, col, ...]
        positions.forEach((pos) => {
          if (pos.col < 1) return; // skip objective column
          if (!byRow[pos.row]) byRow[pos.row] = [];
          byRow[pos.row].push(pos.col);
        });

        const now = new Date().toISOString();

        // Capture undo snapshot before making changes
        const undoEntry: { objectiveUuid: string; previousStatuses: Status1[] }[] = [];

        Object.entries(byRow).forEach(([rowKey, cols]) => {
          const rowIdx = parseInt(rowKey, 10);
          if (rowIdx >= objectivesOnMap.length) return;
          const objective = objectivesOnMap[rowIdx];
          if (!objective?.uuid) return;
          // Parent objectives infer status from children — never write status directly
          if (isParent(objective.uuid)) return;
          // Skip metric objectives when letter status key pressed (data integrity guard)
          // Exception: 'p' (pending) is valid on both deliverables and metrics
          if (isStatusKey && pressedKey !== 'p' && isMetricObjective(objective.type)) return;

          // Save previous state for undo
          undoEntry.push({
            objectiveUuid: objective.uuid,
            previousStatuses: [...(objective.statuses || [])],
          });

          // Start from the current statuses array and apply all column changes via shared helper
          let statuses = [...(objective.statuses || [])];

          cols.forEach((colIdx) => {
            const updated = buildStatusUpdateForCell(
              statuses,
              colIdx,
              visibleIntervals,
              milestoneMap?.interval || 'weeks',
              effectiveZoomLevel,
              key,
              isDeleteKey,
              now,
              userProfile?.userName,
            );
            if (updated !== null) {
              statuses = updated;
            }
          });

          // Single dispatch per objective with all changes merged
          dispatch(
            updateObjective({
              uuid: objective.uuid,
              updates: { statuses },
            }),
          );
        });

        // Push undo entry if any changes were made
        if (undoEntry.length > 0) {
          setUndoStack((prev) => [...prev.slice(-49), undoEntry]); // Keep max 50 entries
        }
      } else {
        // Single cell path — look up the objective once
        const isDigitKey = /^[0-9.]$/.test(key);
        const objective = position.col >= 1 && position.row < objectivesOnMap.length
          ? objectivesOnMap[position.row]
          : null;
        const isMetric = objective && isMetricObjective(objective.type);

        if (isDigitKey && isMetric && objective.kpiSettings && position.col >= 1) {
          // Start or append to metric numeric input
          if (key === '.' && metricInput?.digits.includes('.')) return; // only one decimal
          const currentDigits = metricInput?.row === position.row && metricInput?.col === position.col
            ? metricInput.digits
            : '';
          const newDigits = currentDigits + key;
          setMetricInput({ row: position.row, col: position.col, digits: newDigits });
          setAnnouncement(`Typing: ${newDigits}`);
          return;
        }

        // Permission guard — match StatusCell2's existing canEdit behaviour.
        // Rejection hints (below) are scoped to editable, non-parent cells only:
        // read-only maps and parent rows stay silent per mr-rmit scope carve-out.
        if (!canEdit) return;

        // Rejection hints (Bead: mr-rmit) only apply to editable, non-parent
        // status cells. Read-only maps, parent rows, and the label column
        // remain silent to preserve the existing silent-no-op contract there.
        const isEditableStatusCell =
          position.col >= 1 && !!objective?.uuid && !isParent(objective.uuid);

        if (isEditableStatusCell) {
          // Rejection hint: digit key on a deliverable row.
          // Digits are only meaningful on metric rows (numeric KPI input).
          if (isDigitKey && !isMetric) {
            showKeyboardHint(RMIT_DELIVERABLE_HINT, RMIT_DELIVERABLE_HINT_SR);
            return;
          }

          // Rejection hint: status letter (not 'p') on a metric row.
          // Metric rows only accept p (pending), digits, and '.'.
          if (isStatusKey && pressedKey !== 'p' && isMetric) {
            showKeyboardHint(RMIT_METRIC_HINT, RMIT_METRIC_HINT_SR);
            return;
          }
        }

        // Only act on status cells (col >= 1) with a valid status key or delete key
        if ((isStatusKey || isDeleteKey) && position.col >= 1) {
          if (!objective?.uuid || isParent(objective.uuid)) return;

          const now = new Date().toISOString();
          const newStatuses = buildStatusUpdateForCell(
            objective.statuses || [],
            position.col,
            visibleIntervals,
            milestoneMap?.interval || 'weeks',
            effectiveZoomLevel,
            key,
            isDeleteKey,
            now,
            userProfile?.userName,
          );

          if (newStatuses === null) return;

          // Capture undo state before dispatching
          setUndoStack((prev) => [
            ...prev.slice(-49),
            [{ objectiveUuid: objective.uuid, previousStatuses: [...(objective.statuses || [])] }],
          ]);

          dispatch(
            updateObjective({
              uuid: objective.uuid,
              updates: { statuses: newStatuses },
            }),
          );
        }
      }
    },
    canScrollForward: canGoForward,
    canScrollBack: canGoBack,
    scrollForward: handleNextPage,
    scrollBack: handlePreviousPage,
    scrollEdges: {
      left: 1,
      right: maxStatusColumnCount,
    },
  });

  // --- Metric numeric input: commit/cancel helpers and wrapped handleKeyDown ---
  const commitMetricInput = useCallback(
    (input: { row: number; col: number; digits: string }) => {
      const numericValue = parseFloat(input.digits);
      if (isNaN(numericValue)) {
        setMetricInput(null);
        setAnnouncement('');
        return;
      }

      const now = new Date().toISOString();

      // Determine positions to update: selection (if active) or single cell
      type CellPos = { row: number; col: number };
      let positions: CellPos[];
      if (selection) {
        positions = getSelectedPositions(selection).filter((p) => p.col >= 1);
      } else {
        positions = [{ row: input.row, col: input.col }];
      }

      // Group positions by row (objective), same pattern as bulk status dispatch
      const byRow: Record<number, number[]> = {};
      positions.forEach((pos) => {
        if (!byRow[pos.row]) byRow[pos.row] = [];
        byRow[pos.row].push(pos.col);
      });

      const undoEntry: { objectiveUuid: string; previousStatuses: Status1[] }[] = [];
      let cellCount = 0;

      Object.entries(byRow).forEach(([rowKey, cols]) => {
        const rowIdx = parseInt(rowKey, 10);
        if (rowIdx >= objectivesOnMap.length) return;
        const objective = objectivesOnMap[rowIdx];
        if (!objective?.uuid || !objective.kpiSettings) return;
        if (isParent(objective.uuid)) return;
        // Only apply metric values to metric objectives
        if (!isMetricObjective(objective.type)) return;

        const continuousCode = calculateContinuousCode(numericValue, objective.kpiSettings);

        // Save previous state for undo
        undoEntry.push({
          objectiveUuid: objective.uuid,
          previousStatuses: [...(objective.statuses || [])],
        });

        // Apply metric value to all selected columns in this row
        let statuses = [...(objective.statuses || [])];
        cols.forEach((colIdx) => {
          const statusIndex = colIdx - 1;
          if (statusIndex < 0 || statusIndex >= visibleIntervals.length) return;

          const interval = visibleIntervals[statusIndex];
          const forIntervalEnding = interval.end.toISOString();
          const existingStatus = getStatusForAdjustedInterval(
            statuses,
            interval,
            milestoneMap?.interval || 'weeks',
            effectiveZoomLevel,
          );

          const uuid = existingStatus?.uuid || crypto.randomUUID();
          const newStatus = buildWebStatusWrite(
            existingStatus,
            {
              uuid,
              shortId: existingStatus?.shortId || getShortIdFromUuid(uuid),
              forIntervalEnding,
              statusCode: continuousCode,
              value: numericValue,
            },
            userProfile?.userName,
            now,
          );
          statuses = upsertItem(statuses, newStatus, (s) => s.uuid === uuid);
          cellCount++;
        });

        dispatch(
          updateObjective({
            uuid: objective.uuid,
            updates: { statuses },
          }),
        );
      });

      // Push undo entry if any changes were made
      if (undoEntry.length > 0) {
        setUndoStack((prev) => [...prev.slice(-49), undoEntry]);
      }

      setMetricInput(null);
      setAnnouncement(
        cellCount === 1
          ? `Set metric value to ${numericValue}`
          : `Set metric value to ${numericValue} on ${cellCount} cells`,
      );
    },
    [objectivesOnMap, visibleIntervals, milestoneMap, effectiveZoomLevel, dispatch, selection, isParent, userProfile?.userName],
  );

  // Wrap handleKeyDown to intercept keys during metric numeric input
  const wrappedHandleKeyDown = useCallback(
    (e: React.KeyboardEvent, row: number, col: number) => {
      if (metricInput) {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitMetricInput(metricInput);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMetricInput(null);
          setAnnouncement('');
          return;
        }
        if (e.key === 'Backspace') {
          e.preventDefault();
          const newDigits = metricInput.digits.slice(0, -1);
          if (newDigits.length === 0) {
            setMetricInput(null);
            setAnnouncement('');
          } else {
            setMetricInput({ ...metricInput, digits: newDigits });
            setAnnouncement(`Typing: ${newDigits}`);
          }
          return;
        }
        // Arrow/Tab: commit then let navigation proceed
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
          commitMetricInput(metricInput);
          // fall through to hook's handleKeyDown for navigation
        }
      }
      handleKeyDown(e, row, col);
    },
    [metricInput, commitMetricInput, handleKeyDown],
  );

  // Commit metric input when focus moves away from the input cell
  useEffect(() => {
    if (metricInput && (focusPosition.row !== metricInput.row || focusPosition.col !== metricInput.col)) {
      commitMetricInput(metricInput);
    }
  }, [focusPosition, metricInput, commitMetricInput]);

  // Announce selection changes for screen readers
  useEffect(() => {
    if (selection) {
      const positions = getSelectedPositions(selection);
      const count = positions.length;
      if (count > 1) {
        setAnnouncement(`${count} cells selected`);
      }
    }
  }, [selection]);

  // Undo handler: restore previous status state (uses functional updater to avoid stale closure)
  const handleUndo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;

      const lastEntry = prev[prev.length - 1];

      lastEntry.forEach(({ objectiveUuid, previousStatuses }) => {
        dispatch(
          updateObjective({
            uuid: objectiveUuid,
            updates: { statuses: previousStatuses },
          }),
        );
      });

      const objectiveCount = lastEntry.length;
      setAnnouncement(
        objectiveCount === 1
          ? 'Undid status change'
          : `Undid status changes on ${objectiveCount} objectives`,
      );

      return prev.slice(0, -1);
    });
  }, [dispatch]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

      // Cmd+Z (Mac) or Ctrl+Z (Windows): undo last status change
      if (e.key === 'z' && (isMac ? e.metaKey : e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }

      // Show keyboard help when user presses Command+? on Mac or Ctrl+? on other platforms
      if (e.key === '/' && (isMac ? e.metaKey : e.ctrlKey)) {
        e.preventDefault();
        setShowKeyboardHelp((show) => !show);
      }
      // Close keyboard help when user presses Escape
      if (e.key === 'Escape' && showKeyboardHelp) {
        e.preventDefault();
        setShowKeyboardHelp(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showKeyboardHelp, handleUndo]);

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled Promise Rejection:', {
        reason: event.reason,
        promise: event.promise,
        stack: event.reason?.stack,
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Effect to manage placeholder rows for adding new objectives.
  useEffect(() => {
    if (milestoneMap) {
      // When a new map is created, add some number of placeholder rows
      // to allow help users add objectives. Settign a minimum number of rows
      // to five makes sure we don't have funny looking tables with nothing in them.
      const currentObjectiveCount = milestoneMap.objectiveUuids.length;
      const minMapRows = 5;
      // Let's put one placeholder at the bottom of every milestone map and see if
      // people find this helpful.
      // TODO: Revisit to make this configurable or remove depending on feedback.
      const minPlaceholders = 1;
      const neededPlaceholders = Math.max(minPlaceholders, minMapRows - currentObjectiveCount);

      // Add new placeholders after the last objective
      Array.from({ length: neededPlaceholders }).forEach((_, i) => {
        const afterRow = currentObjectiveCount + i - 1;
        dispatch(insertPlaceholderRow({ afterRow }));
      });
    }
  }, [dispatch, milestoneMap]);

  // Effect to update accessedMilestoneMaps when map is loaded
  useEffect(() => {
    if (!isLoading && milestoneMap && userProfile?.uid) {
      // Check if we've already recorded this map access today
      const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD
      const existingAccess = userProfile.accessedMilestoneMaps?.find(
        (map) =>
          map.shortId === mapShortId && map.lastAccessed && map.lastAccessed.startsWith(today),
      );

      // Only update if we haven't recorded access today
      if (!existingAccess) {
        // Create a new array with the existing maps (excluding current map if it exists)
        const existingMaps = (userProfile.accessedMilestoneMaps || []).filter(
          (map) => map.shortId !== mapShortId,
        );

        // Create new map access entry
        const newMapAccess = {
          uuid: milestoneMap.uuid,
          shortId: milestoneMap.shortId,
          title: milestoneMap.title || '',
          // TODO: Harden this. Throw an error if there is no owner name. This really should never happen.
          // This fallback to userProfile.userName is a hack to keep dummy data working in Alpha.
          // Once we have more real data working consistently, we should remove this fallback.
          ownerName: milestoneMap.owner?.name || userProfile.userName,
          lastAccessed: new Date().toISOString(),
          pinned:
            userProfile.accessedMilestoneMaps?.find((m) => m.shortId === mapShortId)?.pinned ||
            false,
        };

        // Combine into new array
        const updatedAccessedMaps = [...existingMaps, newMapAccess];

        dispatch(
          updateUser({
            uid: userProfile.uid,
            updates: {
              accessedMilestoneMaps: updatedAccessedMaps,
            },
          }),
        );
      }
    }
  }, [dispatch, isLoading, mapShortId, milestoneMap, userProfile]);

  useEffect(() => {
    // Call onReady when Map3 is fully initialized
    onReady?.();
  }, [onReady]);

  // --- COMMIT-ON-DROP REFACTOR ---
  // mr-phav: Use a lazy initializer (matching objectivesOnMap at line 930) so that
  // Reorder.Group.values matches the rendered Reorder.Item children on the very first
  // render. Without this, values starts as [] while children is [A,B,C,D], and Framer
  // Motion's layout-projection tracker treats the second-render update as "items added",
  // playing an unwanted fly-in animation. Aligning values with children on first render
  // eliminates the empty→populated transition and prevents the animation. The useEffect
  // at line 180 remains as the correctness guard for subsequent Redux-driven updates.
  const [pendingObjectivesOrder, setPendingObjectivesOrder] = useState<Objective1[]>(() => {
    if (!mapShortId) return [];
    const initialState = store.getState();
    const initialMap = initialState.map3Data.milestoneMaps.find(
      (m) => m.shortId === mapShortId,
    );
    if (!initialMap?.objectiveUuids) return [];
    return initialMap.objectiveUuids
      .map((uuid) => initialState.map3Data.objectives.find((obj) => obj.uuid === uuid))
      .filter((obj): obj is Objective1 => obj !== undefined);
  });
  const commitCalledRef = useRef(false);
  function handleReorderCommit() {
    if (commitCalledRef.current) return;
    commitCalledRef.current = true;
    const newObjectiveUuids = pendingObjectivesOrder.map((obj) => obj.uuid);
    if (
      !milestoneMap ||
      JSON.stringify(newObjectiveUuids) === JSON.stringify(milestoneMap.objectiveUuids)
    ) {
      // Bug 7382485: Reset commit flag on early return to allow subsequent reorders
      commitCalledRef.current = false;
      return;
    }

    // Bug 7382485: Read current objectiveUuids from Redux at commit time to avoid
    // stale closure dropping newly added objectives. Append any missing UUIDs.
    const currentState = store.getState();
    const currentMap = currentState.map3Data.milestoneMaps.find(
      (m: MilestoneMap1) => m.uuid === milestoneMap.uuid,
    );
    const currentUuids = currentMap?.objectiveUuids || milestoneMap.objectiveUuids;
    const missingUuids = currentUuids.filter((id: string) => !newObjectiveUuids.includes(id));
    const safeObjectiveUuids =
      missingUuids.length > 0 ? [...newObjectiveUuids, ...missingUuids] : newObjectiveUuids;

    // Update the milestone map with the new objective order
    dispatch(
      updateMilestoneMap({
        uuid: milestoneMap.uuid,
        updates: { objectiveUuids: safeObjectiveUuids },
      }),
    );

    // Calculate and apply all parent/child relationship updates based on the new order
    const updatedMilestoneMap = { ...milestoneMap, objectiveUuids: safeObjectiveUuids };
    const relationshipUpdates = calculateObjectiveRelationshipUpdates(
      updatedMilestoneMap,
      pendingObjectivesOrder,
    );

    // Apply the calculated updates to each objective that needs changes
    Object.entries(relationshipUpdates).forEach(([objectiveUuid, updates]) => {
      dispatch(updateObjective({ uuid: objectiveUuid, updates }));
    });

    // TODO: In future, handle multi-user conflict resolution here if needed.

    // Reset commit flag to allow subsequent drag operations
    commitCalledRef.current = false;
  }

  // --- MIGRATION ---
  // Result1: version 1 (unversioned) -> 2 (versioned)
  useEffect(() => {
    if (objectivesOnMap.length === 0) return;
    // For each objective, migrate its results array
    objectivesOnMap.forEach((objective) => {
      if (!objective.results || objective.results.length === 0) return;
      const migratedResults = migrateResult1Array(objective.results);
      // Only update if migration changed something
      if (isArrayDifferentAfterMigration(objective.results, migratedResults)) {
        dispatch(updateObjective({ uuid: objective.uuid, updates: { results: migratedResults } }));
      }
    });
  }, [objectivesOnMap, dispatch]);
  // --- END MIGRATION ---

  return (
    <>
      {isLoading && (
        <div role="status" aria-live="polite" className="text-center py-4">
          Loading milestone map grid...
        </div>
      )}
      {!isLoading && (
        <section
          id="Map3"
          className="w-full overflow-x-auto"
          role="main"
          aria-label="Milestone map grid"
        >
          <div className="sr-only">
            Use arrow keys to navigate the grid. Press Enter or Space to select a cell. You can also
            reorder objectives by dragging them up or down.
          </div>

          <div aria-live="polite" className="sr-only">
            {announcement}
          </div>

          <div className="w-full">
            {/* Inline hint/error strip above the grid (Bead: mr-rmit).
                Min-height reserves space so the grid does not jitter when the
                strip shows or hides. Replaces the previous absolute top-right
                positioning which overlaid cells. */}
            <div className="min-h-[2rem]">
              <Map3ErrorMessage error={keyboardError} severity={keyboardErrorSeverity} />
            </div>

            <div className="relative">
              <Map3DebugIndicator debug={debug} />
              <Map3KeyboardHelp
                show={showKeyboardHelp}
                onClose={() => setShowKeyboardHelp(false)}
              />
              {debug && <Map3DebugPanel debugLog={debugLog} onClear={() => setDebugLog([])} />}

              <div className="sr-only" role="note">
                Press Shift+? for keyboard shortcuts
              </div>

              {/* Metric numeric input overlay */}
              {metricInput && (
                <MetricInputOverlay
                  tableRef={tableRef}
                  row={metricInput.row}
                  col={metricInput.col}
                  digits={metricInput.digits}
                />
              )}

              <table
                ref={tableRef}
                role="grid"
                aria-label="Objectives and Status Timeline Grid"
                aria-rowcount={objectivesOnMap.length} // Leave out placeholder rows. They're not part of the grid from a UX perspective.
                aria-colcount={visibleIntervals.length + 1} // Add one for objectives, but not drag handles. Handles are outside the grid experience.
                className="w-full table-fixed border-collapse"
                style={{ border: '1px solid #e5e7eb' }}
                tabIndex={0}
                onFocus={(e) => {
                  // Provide navigation instructions when table receives focus
                  if (focusPosition.row === -1) {
                    setAnnouncement(
                      'Press Enter to navigate cells, or Tab to skip to page controls.',
                    );
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && focusPosition.row === -1) {
                    // Enter on table: focus first cell to start grid navigation
                    e.preventDefault();
                    handleCellFocus(0, 0);
                    const selector = `[data-row="0"][data-col="0"]`;
                    const cell = tableRef.current?.querySelector(selector) as HTMLElement;
                    cell?.focus();
                  }
                }}
              >
                <caption className="sr-only">{generateTableSummary()}</caption>

                <colgroup>
                  <col className="w-8" />
                  <col className="w-1/3" />
                  <col span={maxStatusColumnCount} className="w-[5.77%]" />
                </colgroup>

                <Map3TableHeader
                  milestoneMapUuid={milestoneMap?.uuid || ''}
                  visibleIntervals={visibleIntervals}
                  maxStatusColumnCount={maxStatusColumnCount}
                  effectiveZoomLevel={effectiveZoomLevel}
                />

                <Map3Tbody
                  objectivesOnMap={objectivesOnMap}
                  setObjectivesOnMap={setObjectivesOnMap}
                  setAnnouncement={setAnnouncement}
                  milestoneMapUuid={milestoneMap?.uuid || ''}
                  pendingObjectivesOrder={pendingObjectivesOrder}
                  setPendingObjectivesOrder={setPendingObjectivesOrder}
                  onDragEndRow={handleReorderCommit}
                >
                  {objectivesOnMap.map((objective, index) => (
                    <Map3Tr
                      key={objective.uuid}
                      milestoneMapUuid={milestoneMap?.uuid || ''}
                      objective={objective}
                      isParent={isParent(objective.uuid)}
                      onDragEndRow={handleReorderCommit}
                    >
                      <Map3TdObjective
                        objective={objective}
                        index={index}
                        focusPosition={focusPosition}
                        hoverPosition={hoverPosition}
                        handleCellFocus={handleCellFocus}
                        handleKeyDown={wrappedHandleKeyDown}
                        setHoverPosition={setHoverPosition}
                        isParent={isParent}
                        milestoneMapUuid={milestoneMap?.uuid || ''}
                        selection={selection}
                        handleSelectionStart={handleSelectionStart}
                        handleSelectionEnd={handleSelectionEnd}
                        drawerIsOpen={drawerIsOpen}
                      />
                      {Array(visibleIntervals.length)
                        .fill(null)
                        .map((_, statusIndex) => (
                          <Map3TdStatus
                            key={statusIndex}
                            objective={objective}
                            index={index}
                            statusIndex={statusIndex}
                            columnOffset={columnOffset}
                            visibleIntervals={visibleIntervals}
                            focusPosition={focusPosition}
                            hoverPosition={hoverPosition}
                            handleCellFocus={handleCellFocus}
                            handleKeyDown={wrappedHandleKeyDown}
                            setHoverPosition={setHoverPosition}
                            isParent={isParent}
                            milestoneMapUuid={milestoneMap?.uuid || ''}
                            selection={selection}
                            handleSelectionStart={handleSelectionStart}
                            handleSelectionEnd={handleSelectionEnd}
                            ariaRowIndex={index + 1}
                            ariaColIndex={statusIndex + 2}
                            originalInterval={milestoneMap?.interval || 'weeks'}
                            effectiveZoomLevel={effectiveZoomLevel}
                            drawerIsOpen={drawerIsOpen}
                          />
                        ))}
                    </Map3Tr>
                  ))}
                  {placeholderRows.map((row, placeholderIndex) => (
                    <Map3TrPlaceholder
                      key={`placeholder-${row}`}
                      index={objectivesOnMap.length + placeholderIndex}
                      columnCount={visibleIntervals.length + 1}
                      milestoneMapUuid={milestoneMap?.uuid || ''}
                      // Below are navigation props to maintain grid keyboard accessibility
                      focusPosition={focusPosition}
                      hoverPosition={hoverPosition}
                      handleCellFocus={handleCellFocus}
                      handleKeyDown={wrappedHandleKeyDown}
                      setHoverPosition={setHoverPosition}
                      handleSelectionStart={handleSelectionStart}
                      handleSelectionEnd={handleSelectionEnd}
                    />
                  ))}
                </Map3Tbody>
              </table>

              <nav role="navigation" aria-label="Adjust timeline">
                <Pager1
                  canGoBack={canGoBack}
                  canGoForward={canGoForward}
                  onPrevious={handlePreviousPage}
                  onNext={handleNextPage}
                />
              </nav>

              <Map3Drawer
                milestoneMapUuid={milestoneMap?.uuid || ''}
                ownerName={milestoneMap?.owner.name || ''}
              >
                {/* Drawer content rendered conditionally by Map3Drawer via ?tab= param */}
                <></>
              </Map3Drawer>

              <PlaylistDialog />
            </div>
          </div>
        </section>
      )}
    </>
  );
}

Map3Tr.displayName = 'Map3Tr';

/**
 * Map3 wrapped in React.memo to prevent re-renders when Map3Workspace
 * re-renders due to its own Redux selector changes (e.g. isLoading, userProfile).
 * Since Map3 receives no props from Map3Workspace (it is mounted via React Router's
 * Outlet), memoization is always effective — Map3 only re-renders when its own
 * internal state or Redux selectors change.
 *
 * Preserves the mr-aqs.20 hidden-Outlet mount stability: Map3 stays mounted across
 * drawer tab switches because Map3Workspace never unmounts it.
 */
export const Map3 = React.memo(Map3Component);
